// Callpage-ref: components/Layout.tsx (uproszczona wersja navbara — bez tabs, notifications, company switcher).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';

import { GlobalSearch } from '@/components/tenant/GlobalSearch';
import { GlobalSearchModal } from '@/components/tenant/GlobalSearchModal';
import { type AdminRole, useAuth } from '@/contexts/AuthContext';

import { Badge, type BadgeColor } from './Badge';
import { Button } from './Button';
import { ThemePresetToggle } from './ThemePresetToggle';
import { ThemeToggle } from './ThemeToggle';

interface TopbarProps {
  title?: string;
}

const ROLE_BADGES: Record<AdminRole, { label: string; color: BadgeColor }> = {
  super_admin: { label: 'Super Admin', color: 'accent' },
  support: { label: 'Support', color: 'blue' },
  finance: { label: 'Finanse', color: 'green' },
  readonly: { label: 'Tylko odczyt', color: 'gray' },
};

export function Topbar({ title = 'SaaS v2 — panel admina' }: TopbarProps) {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);

  // Globalny skrót Ctrl/Cmd+K — otwiera paletę wyszukiwarki panelu ITE.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const onLogout = async () => {
    await logout();
    navigate('/login');
  };

  const roleInfo = admin?.role ? ROLE_BADGES[admin.role] : null;

  return (
    <>
      <GlobalSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} scope="admin" />
      <header className="flex h-14 items-center justify-between gap-3 border-b border-border-secondary bg-bg-elevated px-6">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="truncate text-sm font-semibold text-text-primary">{title}</h1>
        </div>

        <div className="flex flex-1 justify-center px-4">
          <div className="w-full max-w-md">
            <GlobalSearch layout="inline" scope="admin" />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {admin && (
            <div className="hidden sm:flex items-center gap-2 border-r border-border-secondary pr-3 mr-1">
              <div className="text-right">
                <div className="text-sm font-medium text-text-primary leading-tight">
                  {admin.name ?? admin.email}
                </div>
                {admin.name && (
                  <div className="text-xs text-text-muted leading-tight">{admin.email}</div>
                )}
              </div>
              {roleInfo && <Badge color={roleInfo.color}>{roleInfo.label}</Badge>}
            </div>
          )}

          <ThemePresetToggle />
          <ThemeToggle />

          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            leftIcon={<LogOut size={16} aria-hidden="true" />}
          >
            Wyloguj
          </Button>
        </div>
      </header>
    </>
  );
}
